import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { HttpInterceptorProviders } from './core/interceptors';
import { InitialPageGuardService } from './core/router/initial-page-guard.service';
import { coreReducer } from './core/store/core.reducer';
import { CoreStore } from './core/store/core.store';
import { AccountModule } from './modules/account/account.module';
import { LoginModule } from './modules/login/login.module';
import { LoginState } from './modules/login/services/login.service';
import { SharedModule } from './shared/shared.module';
import { normalizeLocaleName } from '../../../../scripts/deploy/locale';
import { environment } from '../environments/environment';
import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  API_USER_GQL_URL,
  APP_URL,
  AuthModule,
  AuthStore,
  CUSTOMER_PORTAL_URL,
  ErrorHandlerService,
  IGNORE_SENTRY_ERRORS,
  ignoredSentryErrors,
  LOGICAL_USER_ERRORS,
  logicalUserErrors,
  PIPELINER_TOKEN,
  SENTRY_ENABLED,
  SSO_URL
} from '@ppl/auth';
import { CoreModule as PplCoreModule } from '@ppl/core';
import { FIREBASE_CONFIG, FirebaseModule, FirebaseService } from '@ppl/firebase';
import { I18nService } from '@ppl/i18n';
import { PplUiIntl } from '@ppl/ui/intl';
import { PIPELINER_NG_UI_THEME } from '@ppl/ui/tokens';
import { DEPLOY_URL } from '@ppl/utils/injection-tokens';
import * as Sentry from '@sentry/browser';
import type { User } from './modules/login/store/login.reducer';
import type { TwoFAMethod, TwoFAType } from './shared/modules/two-factor/domain/two-factor';


if (environment.metrics.sentryEnabled) {
  Sentry.init({
    dsn: 'https://3dca6df6cd08434c8d29621d45d8af83@sentry.io/1387005',
    release: environment.version,
    environment: environment.name
  });
}

@NgModule()
export class FakeModule {
}

export function storeFactory() {
  const store = new CoreStore();

  if (environment.toolsInWindow) {
    (window as any).store = store;
  }

  return store;
}

export interface AppState {
  jwt: string;
  token?: string;
  // Below two properties are used on welcome screen
  can_change_password?: boolean;
  must_change_password?: boolean;
  // Below property is for email verification screen
  email_verified?: boolean;
  state?: LoginState;
  username?: string;
  ui: 'popup' | 'default';
  teamspace_id: number;
  error?: number;
  error_str?: string;
  logged_in?: boolean;
  user?: User;
  // NOTE(mike,2fa): Present only when 2FA
  idp_id?: number;
  two_fa_methods?: TwoFAMethod[];
  required_two_fa_methods?: TwoFAType[] | null;
  extra: {
    description?: string,
    disable_google_sign_in?: boolean,
    disable_microsoft_sign_in?: boolean
  } | null;
  login_methods: {
    qr?: {
      enabled?: boolean;
    };
  };
}

export function initAppStateFactory(store: CoreStore) {
  const STATE_TO_URL = {
    [LoginState.SUCCESS]: '/',
    [LoginState.SELECT_TEAMSPACE]: 'select-space'
  };
  return () => {
    const appStateElement = document.querySelector('#app-state');
    const content = appStateElement.textContent.trim();
    let appState: AppState;
    try {
      appState = content ? JSON.parse(content) : null;
    } catch (e) {
      appState = null;
    }

    if (appState) {
      // Extract required data.
      store.dispatch('Login_InitState', {
        jwt: appState.jwt,
        welcomeScreen: {
          can_change_password: !!appState.can_change_password,
          must_change_password: !!appState.must_change_password
        },
        emailVerificationScreen: {
          email_verified: !!appState.email_verified
        },
        mobileSignInEnabled: !!appState.login_methods?.qr?.enabled,
        ui: appState.ui,
        teamspace_id: appState.teamspace_id,
        extra: appState.extra
      });

      if (appState.error) {
        store.dispatch('Login_SetError', {
          error: appState.error,
          error_str: appState.error_str
        });
      }
      if (appState.username) {
        store.dispatch('Login_SetLoginUserEmail', appState.username);
      }
      if (appState.two_fa_methods) {
        store.dispatch('Login_SetLoginTwoFAMethods', appState.two_fa_methods);
      }
      if (appState.required_two_fa_methods) {
        store.dispatch('Login_SetLoginRequiredTwoFAMethods', appState.required_two_fa_methods);
      }
      if ('idp_id' in appState) {
        store.dispatch('Login_SetLoginUserIDP', appState.idp_id);
      }
      if ('logged_in' in appState) {
        store.dispatch('Login_SetLoginUserIsLoggedIn', appState.logged_in);
      }
      if (appState.user) {
        store.dispatch('Login_SetUser', appState.user);
      }
      // Set initial url we should route to.
      const url = STATE_TO_URL[appState.state] || null;
      store.dispatch('Router_SetInitialUrl', url);
    }
  };
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserAnimationsModule,
        PplCoreModule,
        CoreModule,
        AuthModule,
        SharedModule,
        AccountModule,
        LoginModule,
        AppRoutingModule,
        {
            ngModule: FakeModule,
            providers: [{ provide: CoreStore, useFactory: storeFactory }]
        },
        {
            ngModule: FirebaseModule,
            providers: [FirebaseService, { provide: FIREBASE_CONFIG, useValue: environment.metrics.firebase }]
        }], providers: [
        ...HttpInterceptorProviders,
        InitialPageGuardService,
        { provide: AuthStore, useExisting: CoreStore },
        { provide: APP_INITIALIZER, multi: true, useFactory: initAppStateFactory, deps: [CoreStore] },
        { provide: APP_BASE_HREF, useValue: environment.baseUrl },
        { provide: APP_URL, useValue: '' },
        { provide: API_USER_GQL_URL, useValue: environment.apiUserGqlUrl },
        { provide: CUSTOMER_PORTAL_URL, useValue: environment.customerPortalUrl },
        {
            // NOTE(mike): Use deploy url with `locale` only when deploying (e.g. staging, prod, ...)
            // not when developing locally.
            provide: DEPLOY_URL, useValue: environment.production
                ? `${environment.deployUrl}/${normalizeLocaleName($localize.locale)}`
                : environment.deployUrl
        },
        { provide: SENTRY_ENABLED, useValue: environment.metrics.sentryEnabled },
        { provide: SSO_URL, useValue: '' },
        { provide: PIPELINER_TOKEN, useValue: environment.pipelinerToken },
        { provide: LOGICAL_USER_ERRORS, useValue: logicalUserErrors },
        { provide: IGNORE_SENTRY_ERRORS, useValue: ignoredSentryErrors },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        { provide: PIPELINER_NG_UI_THEME, useValue: 'pipeliner' },
        {
            provide: PplUiIntl,
            deps: [I18nService],
            useFactory: pplUiIntlFactory
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
  constructor(store: CoreStore) {
    store.setRootReducer(coreReducer);
  }
}

export function pplUiIntlFactory(i18nService: I18nService) {
  const ngUiIntl = new PplUiIntl();
  Object.entries(ngUiIntl).forEach(([key, text]) => ngUiIntl[key] = i18nService.translate(text));
  return ngUiIntl;
}
